import { savePropertyInterest } from '../services/propertyInterestService';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../config';
import './AddContact.css';

const AddContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [openHouseMlsKey, setOpenHouseMlsKey] = useState('');

  useEffect(() => {
    // Fetch the current open house MLS key
    const fetchOpenHouseMlsKey = async () => {
      try {
        const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, {
          headers: {
            'X-API-Key': 'treas-api-key-2025'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setOpenHouseMlsKey(data.openHouseMlsKey || '');
        }
      } catch (error) {
        console.error('Error fetching open house MLS key:', error);
      }
    };

    fetchOpenHouseMlsKey();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Step 1: Add contact
      const addContactResponse = await fetch('https://tophamtechbe.com/treas-postgrid/s3/add-address', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          address: {
            Name: formData.name,
            PhoneNumber: formData.phoneNumber,
            Email: formData.email
          }
        })
      });

      if (!addContactResponse.ok && addContactResponse.status !== 409) {
        throw new Error('Failed to add contact');
      }

      // Step 2: Like the property (if MLS key exists)
      if (openHouseMlsKey) {
        await savePropertyInterest({ listing_key: openHouseMlsKey }, formData);
      }

      // Success - show thank you message
      setShowThankYou(true);

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (showThankYou) {
    return (
      <div className="add-contact-page">
        <Helmet>
          <title>Thank You - {SITE_CONFIG.AGENT.NAME}</title>
        </Helmet>

        <div className="thank-you-container">
          <div className="thank-you-card">
            <div className="success-icon-large">✅</div>
            <h1>Thank You!</h1>
            <p>We've received your information and you've been added to our contact list.</p>
            <p>We'll be in touch soon about this property and other opportunities that match your interests.</p>

            <div className="agent-contact-info">
              <h3>Contact Information</h3>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon">👤</span>
                  <span>{SITE_CONFIG.AGENT.NAME}</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>{SITE_CONFIG.AGENT.EMAIL}</a>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>{SITE_CONFIG.AGENT.PHONE}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-contact-page">
      <Helmet>
        <title>Contact Form - {SITE_CONFIG.AGENT.NAME}</title>
        <meta name="description" content="Connect with a professional real estate agent. Leave your contact information to get started." />
      </Helmet>

      <div className="add-contact-container">
        <div className="add-contact-card">
          <div className="card-header">
            <div className="header-icon">📝</div>
            <h1>Connect With Us</h1>
            <p>Leave your contact information and we'll be in touch soon.</p>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your email address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber" className="form-label">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your phone number"
                required
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </form>

          <div className="agent-info">
            <div className="agent-avatar">
              <img src="/avatar.png" alt={SITE_CONFIG.AGENT.NAME} />
            </div>
            <div className="agent-details">
              <h3>{SITE_CONFIG.AGENT.NAME}</h3>
              <p>Real Estate Professional</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContact;